import type { Scene } from "@babylonjs/core/scene";
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { BaseTexture } from "@babylonjs/core/Materials/Textures/baseTexture";

/**
 * Plain dark-blue sea with an animated shoreline: instead of a depth pre-pass,
 * the shader reads the baked terrain heightmap to know the water depth at every
 * point, and draws a foam band that laps in and out along the coast. Open water
 * stays a simple flat blue. Cheap (one texture read), no extra render target —
 * VR-friendly.
 */
Effect.ShadersStore["waterVertexShader"] = /* glsl */ `
precision highp float;
attribute vec3 position;
uniform mat4 viewProjection;
uniform mat4 world;
varying vec3 vWorld;
void main() {
  vec4 wp = world * vec4(position, 1.0);
  vWorld = wp.xyz;
  gl_Position = viewProjection * wp;
}
`;

Effect.ShadersStore["waterFragmentShader"] = /* glsl */ `
precision highp float;
varying vec3 vWorld;
uniform float time;
uniform float worldSize;
uniform float waterLevel;
uniform float elevScale;
uniform vec3 deepColor;
uniform vec3 foamColor;
uniform sampler2D heightMap;
void main() {
  vec2 uv = (vWorld.xz + worldSize * 0.5) / worldSize;
  float inside = step(0.0, uv.x) * step(uv.x, 1.0) * step(0.0, uv.y) * step(uv.y, 1.0);
  float elev = texture2D(heightMap, uv).r * elevScale;
  float depth = waterLevel - elev;

  // A foam band whose depth slowly advances and recedes, surf lapping the coast.
  float w = 0.5 + 0.5 * sin(time * 0.55);
  float bandCenter = mix(0.03, 0.42, w);
  float band = exp(-pow(abs(depth - bandCenter) / 0.08, 2.0));
  float wob = 0.5 + 0.5 * sin(vWorld.x * 0.6 + vWorld.z * 0.5 + time * 1.1);
  band *= 0.55 + 0.45 * wob;
  float edge = 1.0 - smoothstep(0.0, 0.09, depth);
  float foam = clamp(max(edge, band), 0.0, 1.0) * inside;
  foam *= smoothstep(-0.02, 0.05, depth);

  vec3 col = mix(deepColor, foamColor, foam);
  gl_FragColor = vec4(col, 0.94);
}
`;

export function createWaterMaterial(
  scene: Scene,
  heightMap: BaseTexture,
  worldSize: number,
  waterLevel: number,
  elevScale: number,
): ShaderMaterial {
  const mat = new ShaderMaterial("water", scene, "water", {
    attributes: ["position"],
    uniforms: ["world", "viewProjection", "time", "worldSize", "waterLevel", "elevScale", "deepColor", "foamColor"],
    samplers: ["heightMap"],
  });
  mat.setTexture("heightMap", heightMap);
  mat.setFloat("worldSize", worldSize);
  mat.setFloat("waterLevel", waterLevel);
  mat.setFloat("elevScale", elevScale);
  mat.setColor3("deepColor", new Color3(0.10, 0.30, 0.45)); // sea blue
  mat.setColor3("foamColor", new Color3(0.82, 0.88, 0.9)); // soft white foam
  mat.setFloat("time", 0);
  mat.alpha = 0.94;
  mat.backFaceCulling = false;
  scene.onBeforeRenderObservable.add(() => mat.setFloat("time", performance.now() / 1000));
  return mat;
}
